import { useEffect, useState } from "react";
import Axios from "axios";
import { PlaidLinkOptions, usePlaidLink } from "react-plaid-link";
import { getTokenSilently } from "react-auth0-spa";

/** Bank account chosen via Plaid, in the shape CheckoutV2's direct_debit wants. */
export interface PlaidToken {
  token: string;
  accountId: string | null;
  mask: string | null;
}

/**
 * Plaid Link integration for the redesigned donate page's "Checking Account"
 * (ACH / direct_debit) option. Ports the legacy PaymentStep flow: fetch a Plaid
 * link token from /checkout/plaid-link, open Plaid Link on demand, and expose
 * the selected account so the caller can send it to /checkout/v2 as
 * `paymentMethod: { type: "direct_debit", token, accountId }`.
 */
export function useAchPlaid() {
  const [linkToken, setLinkToken] = useState("");
  const [plaidToken, setPlaidToken] = useState<PlaidToken | null>(null);
  const [config, setConfig] = useState<PlaidLinkOptions>({} as PlaidLinkOptions);

  // Fetch the Plaid link token once. A signed-in donor's access token is passed
  // through when available; signed-out donors get a token without one.
  useEffect(() => {
    let active = true;
    const fetchLinkToken = (accessToken?: string) =>
      Axios.post(
        `${process.env.REACT_APP_API_ENDPOINT}/checkout/plaid-link`,
        { accessToken: accessToken ?? undefined },
        { headers: { "x-api-key": process.env.REACT_APP_API_KEY as string } }
      ).then((res) => {
        if (active) setLinkToken(res.data.link_token);
      });

    getTokenSilently()
      .then((accessToken) => fetchLinkToken(accessToken ?? undefined))
      .catch(() => fetchLinkToken(undefined));

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!linkToken) return;
    setConfig({
      token: linkToken,
      publicKey: process.env.REACT_APP_PLAID_PUBLIC_KEY as string,
      clientName: "The Jazz Groove",
      product: ["auth"],
      env: process.env.REACT_APP_NODE_ENV === "dev" ? "sandbox" : "production",
      accountSubtypes: { depository: ["checking", "savings"] },
      onSuccess: (token, metadata) => {
        const account = metadata.accounts?.[0];
        setPlaidToken({
          token,
          accountId: account ? account.id : null,
          mask: account ? account.mask : null,
        });
      },
    } as PlaidLinkOptions);
  }, [linkToken]);

  const plaidLink = usePlaidLink(config);

  return {
    /** Whether Plaid Link is ready to open. */
    ready: plaidLink.ready,
    /** Opens the Plaid Link modal for the donor to connect their bank. */
    open: plaidLink.open,
    /** The connected bank account (or null until one is chosen). */
    plaidToken,
    /** Clears the connected account (e.g. to reconnect a different bank). */
    resetPlaidToken: () => setPlaidToken(null),
  };
}
